import React from "react";
import { Translate, Globe, Check } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, setLanguage } from "@/i18n";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const onPick = async (code) => {
    setLanguage(code);
    try { await api.patch("/auth/preferences", { ui_language: code }); } catch {}
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher-btn"
          aria-label={t("topbar.changeLanguage")}
          title={t("topbar.changeLanguage")}
          className={cn(
            "rounded-md flex items-center gap-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors",
            compact ? "h-10 w-10 justify-center" : "h-10 px-2.5",
          )}
        >
          <span aria-hidden className="text-base leading-none">{current.flag}</span>
          {!compact && (
            <span className="text-xs font-semibold uppercase tracking-wider">{current.code}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Globe size={14} weight="duotone" />
          <span>{t("topbar.language")}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng.code}
            data-testid={`lang-option-${lng.code}`}
            onClick={() => onPick(lng.code)}
            className="flex items-center justify-between gap-2 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span aria-hidden className="text-base">{lng.flag}</span>
              <span className="text-sm">{lng.native}</span>
            </span>
            {i18n.language === lng.code && <Check size={14} weight="bold" className="text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
